import React, { useState, useEffect } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';


import { Stack } from 'ui/stack';
import { mergeSx } from 'ui/merge-sx';

import MarkdownComponent from 'containers/user/learn/components/markdown/markdown';

import Icons from 'assets/icons';

interface CalloutDef {
  type: string;
  icon: React.ReactNode;
  colorSx: SxProps<Theme>;
  calloutNameColorSx: SxProps<Theme>;
}

const CALLOUT_SX: SxProps<Theme> = {
  borderRadius: '0.375rem',
  p: 3,
  width: '100%',
  '@media (min-width: 375px)': { mb: 3 },
  '@media (min-width: 960px)': { mb: 4 },
};

const CALLOUT_NAME_SX: SxProps<Theme> = { alignItems: 'center', pb: 0.5 };

const SUCCESS_COLOR_SX: SxProps<Theme> = { bgcolor: 'tokens.success.100' };
const SUCCESS_NAME_COLOR_SX: SxProps<Theme> = { color: 'tokens.success.800' };
const NOTE_COLOR_SX: SxProps<Theme> = { bgcolor: 'tokens.amber.100' };
const NOTE_NAME_COLOR_SX: SxProps<Theme> = { color: 'tokens.amber.950' };
const WARNING_COLOR_SX: SxProps<Theme> = { bgcolor: 'tokens.critical.100' };
const WARNING_NAME_COLOR_SX: SxProps<Theme> = { color: 'tokens.critical.950' };

const CALLOUTS: CalloutDef[] = [
  {
    type: 'tip',
    icon: <Icons.Active aria-label="tip-icon" />,
    colorSx: SUCCESS_COLOR_SX,
    calloutNameColorSx: SUCCESS_NAME_COLOR_SX,
  },
  {
    type: 'note',
    icon: <Icons.Info aria-label="note-icon" />,
    colorSx: NOTE_COLOR_SX,
    calloutNameColorSx: NOTE_NAME_COLOR_SX,
  },
  {
    type: 'warning',
    icon: <Icons.Warning aria-label="warning-icon" />,
    colorSx: WARNING_COLOR_SX,
    calloutNameColorSx: WARNING_NAME_COLOR_SX,
  },
];

interface CalloutProps {
  variant?: string;
  text?: string;
}

const Callout = ({
  variant = 'tip',
  text = 'Some text',
}: CalloutProps): React.JSX.Element => {
  const [callout, setCallout] = useState<Omit<CalloutDef, 'type'> | undefined>({
    icon: <Icons.Active aria-label="tip-icon" />,
    colorSx: SUCCESS_COLOR_SX,
    calloutNameColorSx: SUCCESS_NAME_COLOR_SX,
  });

  useEffect(() => {
    setCallout(CALLOUTS.find((el) => el.type === variant));
  }, []);

  return (
    <Stack sx={mergeSx(CALLOUT_SX, callout?.colorSx)}>
      <Stack
        direction="row"
        sx={mergeSx(CALLOUT_NAME_SX, callout?.calloutNameColorSx)}
      >
        {callout?.icon}
        <div className="pl-2 font-semibold capitalize-first">{variant}</div>
      </Stack>
      <MarkdownComponent text={text} />
    </Stack>
  );
};

export default Callout;
